import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, NAV_SECTION_PATHS } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FilePlus2, ListTodo, CalendarDays, Briefcase, Truck, PackageSearch, Boxes, ShoppingCart, Users, Zap,
  ClipboardPlus, UserPlus, Pencil, Check, FileText,
} from "lucide-react";

const LINKS = [
  { key: "add_job", to: "/tasks?new=1", label: "Add job", icon: ClipboardPlus, perm: ["orders.manage"] },
  { key: "add_customer", to: "/users?new=1", label: "Add customer", icon: UserPlus, perm: ["customers.edit"] },
  { key: "add_quote", to: "/quotes?new=1", label: "Add quote", icon: FilePlus2, perm: ["quotes.manage"] },
  { key: "jobs", to: "/tasks", label: "Jobs", icon: ListTodo, perm: ["orders.view", "operations.view"] },
  { key: "quotes", to: "/quotes", label: "Quotes", icon: FileText, perm: ["quotes.view"] },
  { key: "scheduler", to: "/scheduler", label: "Scheduler", icon: CalendarDays, perm: ["operations.view"] },
  { key: "job_pool", to: "/job-pool", label: "Job Pool", icon: Briefcase, perm: ["work_bank.view"] },
  { key: "load_board", to: "/load-board", label: "Load Board", icon: Truck, perm: ["mobile.warehouse.movements", "deliveries.view", "operations.view"] },
  { key: "product_requests", to: "/stock/product-requests", label: "Product requests", icon: PackageSearch, perm: ["stock.view", "products.view", "operations.view", "orders.view"] },
  { key: "stock", to: "/stock", label: "Stock", icon: Boxes, perm: ["stock.view"] },
  { key: "epos", to: "/epos", label: "EPOS", icon: ShoppingCart, perm: ["epos.view"] },
  { key: "customers", to: "/users", label: "Customers", icon: Users, perm: ["customers.view"] },
];
const DEFAULT = ["add_job", "add_customer", "add_quote", "jobs", "scheduler", "load_board", "product_requests", "stock", "customers"];

export function QuickLinksCard() {
  const { canAny, navHidden } = useAuth();
  const [chosen, setChosen] = useState(null);
  const [editing, setEditing] = useState(false);
  const path = (to) => to.split("?")[0];
  const blocked = (to) => [...(navHidden || [])].some((k) => (NAV_SECTION_PATHS[k] || []).some((p) => path(to) === p || path(to).startsWith(`${p}/`)));
  const allowed = LINKS.filter((l) => canAny(l.perm) && !blocked(l.to));

  useEffect(() => { api.get("/me/quick-links").then((r) => setChosen(r.data.links ?? DEFAULT)).catch(() => setChosen(DEFAULT)); }, []);

  const toggle = (key) => {
    const next = chosen.includes(key) ? chosen.filter((k) => k !== key) : [...chosen, key];
    setChosen(next);
    api.put("/me/quick-links", { links: next }).catch(() => {});
  };

  if (!chosen) return null;
  const shown = allowed.filter((l) => chosen.includes(l.key));
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 h-full" data-testid="quick-links-card">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Zap className="w-4 h-4" /></span>
          <h2 className="text-base font-bold text-slate-900">Quick links</h2>
        </div>
        <button onClick={() => setEditing(!editing)} data-testid="quick-links-edit-btn"
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-indigo-300 hover:text-indigo-700 transition-colors">
          {editing ? <><Check className="w-3.5 h-3.5" /> Done</> : <><Pencil className="w-3.5 h-3.5" /> Edit</>}
        </button>
      </div>
      {editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="quick-links-editor">
          {allowed.map(({ key, label, icon: Icon }) => (
            <label key={key} className="flex items-center gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50">
              <Checkbox checked={chosen.includes(key)} onCheckedChange={() => toggle(key)} data-testid={`quick-links-toggle-${key}`} />
              <Icon className="w-4 h-4 text-slate-400" /> {label}
            </label>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="quick-links-empty">No shortcuts yet — click Edit to add some.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {shown.map(({ key, to, label, icon: Icon }) => (
            <Link key={key} to={to} data-testid={`quick-link-${key}`}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-2 py-3 text-xs font-semibold text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
              <Icon className="w-5 h-5" /> {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
